/**
 * @file supplierCredits.js
 * @version 1.5.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-08
 * @modified 2026-10-14
 *
 * Supplier credits on the screens (Cam, 8 Oct 2026) — the server's rules are
 * services/supplierCreditService.js and utils/supplierCreditRules.js; these
 * only decide what a row or button offers and what the allocation window
 * lets through before the server checks again.
 *
 *   - Add Supplier Credit is Add Bill in its credit mode: the entry points
 *     (Search Bills' header, a bill's "Add credit against this bill") leave
 *     what to start with in sessionStorage and open 'create-bill'.
 *   - A credit's POs and bills are a reference only: a credit never bills a
 *     PO, never changes a supplied quantity, and never changes a PO's cost
 *     (1.3.0, Cam 2026-10-08 — the "Update PO from credit" offer is gone: a
 *     credit is a one-off amount that its allocation takes off the bill).
 *   - Allocating: the total no more than the credit has left, each amount no
 *     more than the bill owes; both documents in Xero.
 *   - 1.1.0: what a credit is for — goods returned or a price correction —
 *     the user's choice on every credit (never defaulted), fixed once the
 *     credit is in Xero (its lines went there with or without item codes).
 *   - 1.4.0 (Jade, 2026-10-13 — "Add Supplier Credit to Bill"): a credit
 *     raised against ONE bill (againstBillOf) offers to clear that bill's
 *     query and allocate to it — min(the credit, what the bill owes) once both
 *     are in Xero (againstBillSummary, againstBillResultMessage,
 *     autoAllocationText); Search POs' "Add supplier credit" opens it with the
 *     PO and its goods bills named (addCreditPoRowBlock, creditPrefillFromPo).
 *   - 1.5.0 (Cam, 2026-10-14 — "it would not allow me to choose the right
 *     bill"): the bills a credit relates to, and the allocation window's
 *     rows, are searched on the server. BILL_SEARCH_FIELDS is what that
 *     search looks in (SearchHelp's list — the server's billWordSearch in
 *     services/supplierCreditService.js; change them together),
 *     billOptionLabel one bill as a picker shows it, moreToNarrowText the
 *     "showing N of M" line.
 */

import { formatCurrency } from './currencyFormatter';
import { formatDate } from './dateFormatters';
import { billStatusLabel } from './billStatus';

/** sessionStorage key: Add Bill opens in credit mode with this prefill. */
export const CREDIT_MODE_KEY = 'createBill.creditMode';

const up = (v) => String(v || '').trim().toUpperCase();
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};
export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Is this bills row a supplier credit note, overpayment or prepayment? */
export function isSupplierCredit(row) {
  return !!row && ['CREDIT', 'OVERPAYMENT', 'PREPAYMENT'].includes(up(row.transaction_type || 'BILL'));
}

/** Is this bills row a supplier credit NOTE (the kind entered here)? */
export function isCreditNote(row) {
  return !!row && up(row.transaction_type) === 'CREDIT';
}

/** A real Xero id (not a TEMP-/LOCAL- placeholder). */
export function inXero(row) {
  const id = String(row?.xero_bill_id || '').trim();
  return !!id && !/^(TEMP|LOCAL)-/i.test(id);
}

/** What is left of a credit to allocate. */
export function creditRemaining(row) {
  return round2(Math.abs(num(row?.total) || 0) - (num(row?.amount_paid) || 0));
}

/** What is still owed on a bill. */
export function amountDue(row) {
  return round2((num(row?.total) || 0) - (num(row?.amount_paid) || 0));
}

/**
 * Open Add Bill as Add Supplier Credit. `prefill`: { account_id,
 * xero_contact_id, supplier_name, bill: { id, supplier_reference } } — from a
 * bill's "Add credit against this bill" (the supplier, and that bill named);
 * or { …, pos: [{ id, purchase_order_number, xero_purchase_order_id }],
 * bills: [{ id, supplier_reference }] } from a PO (creditPrefillFromPo).
 */
export function startSupplierCredit(onNavigate, prefill = {}) {
  try {
    sessionStorage.setItem(CREDIT_MODE_KEY, JSON.stringify({ type: 'CREDIT', ...prefill }));
  } catch (_) { /* a private window: the screen opens as Add Bill */ }
  if (onNavigate) onNavigate('create-bill');
}

/** Read (and clear) the credit mode Add Bill was opened with, or null. */
export function takeCreditMode() {
  try {
    const raw = sessionStorage.getItem(CREDIT_MODE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(CREDIT_MODE_KEY);
    const v = JSON.parse(raw);
    return v && up(v.type) === 'CREDIT' ? v : null;
  } catch (_) {
    return null;
  }
}

/** Why "Add credit against this bill" is greyed on a row, or null. */
export function addCreditRowBlock(row) {
  if (!row) return 'No bill';
  if (isSupplierCredit(row)) return 'This is a credit — a credit is added against a bill';
  const st = up(row.status);
  if (st === 'VOIDED' || st === 'DELETED') return `This bill is ${st.toLowerCase()}`;
  return null;
}

/**
 * Why "Allocate to bills…" (a credit) / "Apply a credit…" (a bill) is greyed
 * on a row, or null. The window says more (each bill or credit it lists).
 */
export function allocateRowBlock(row) {
  if (!row) return 'Nothing to allocate';
  if (row.allocations_ready === false) return 'Allocating credits here needs the supplier credits migration, which has not been run yet';
  const st = up(row.status);
  if (st === 'VOIDED' || st === 'DELETED') return `This is ${st.toLowerCase()}`;
  if (st === 'DRAFT' || st === 'SUBMITTED') return 'Approve it first — Xero allocates only approved documents';
  if (up(row.currency_code || 'GBP') !== 'GBP') return 'Only sterling documents are allocated here';
  if (!inXero(row)) return 'Not in Xero yet — it is allocated there, so it must be sent first';
  if (isSupplierCredit(row)) {
    if (creditRemaining(row) < 0.005) return 'Nothing left on this credit to allocate';
    return null;
  }
  if (up(row.transaction_type || 'BILL') !== 'BILL') return 'Not a bill';
  if (amountDue(row) < 0.005) return 'Nothing left to pay on this bill';
  return null;
}

/** The row menu's words for allocating, by what the row is. */
export function allocateLabel(row) {
  return isSupplierCredit(row) ? 'Allocate to bills…' : 'Apply a credit…';
}

/**
 * The allocation window's figures, before the server checks again.
 *
 * @param {object} args
 * @param {'credit'|'bill'} args.kind — what the window was opened from
 * @param {number} args.limit — the credit's remaining (kind 'credit') or the
 *        bill's amount due (kind 'bill')
 * @param {Array<{ id, amount_due?, credit_remaining?, block? }>} args.rows — the candidates
 * @param {Object<string, string|number>} args.amounts — typed amounts by row id
 * @returns {{ total: number, over: boolean, rowsOver: number[], blockedTyped: number[], chosen: Array<{ id: number, amount: number }>, ok: boolean }}
 */
export function allocationTotals({ kind = 'credit', limit = 0, rows = [], amounts = {} } = {}) {
  const chosen = [];
  const rowsOver = [];
  const blockedTyped = [];
  let total = 0;
  for (const r of rows || []) {
    const a = round2(num(amounts[r.id]) || 0);
    if (a <= 0) continue;
    if (r.block) { blockedTyped.push(Number(r.id)); continue; }
    const cap = kind === 'credit' ? num(r.amount_due) : num(r.credit_remaining);
    if (cap !== null && a - cap > 0.005) rowsOver.push(Number(r.id));
    chosen.push({ id: Number(r.id), amount: a });
    total = round2(total + a);
  }
  const over = total - (num(limit) || 0) > 0.005;
  return { total, over, rowsOver, blockedTyped, chosen, ok: chosen.length > 0 && !over && !rowsOver.length && !blockedTyped.length };
}

/** The request the server takes, from the window's chosen rows. */
export function allocationPayload(kind, chosen, date) {
  return {
    date: date || undefined,
    allocations: (chosen || []).map(c => (kind === 'credit'
      ? { bill_id: c.id, amount: c.amount }
      : { credit_id: c.id, amount: c.amount }))
  };
}

/** Today as YYYY-MM-DD in the office's day (the browser's local day). */
export function todayIso(d = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** The default allocation date: today, never before the lock's first open day. */
export function defaultAllocationDate(firstAllowedDate, today = todayIso()) {
  return firstAllowedDate && today < firstAllowedDate ? firstAllowedDate : today;
}

/** What a supplier credit can be for, in the screen's words (server: utils/supplierCreditRules). */
export const CREDIT_PURPOSES = Object.freeze([
  { value: 'return', label: 'Goods returned', hint: 'Its items go to Xero and the stock goes down' },
  { value: 'price', label: 'Price correction', hint: 'The account only — stock is not touched' }
]);

/** A stored credit's purpose: the column, else the copy kept before the migration. */
export function creditKindOf(row) {
  const k = String(row?.credit_kind || row?.xero_push_info?.credit_kind || '').toLowerCase();
  return k === 'return' || k === 'price' ? k : null;
}

/** "Goods returned" / "Price correction", or null. */
export function creditKindLabel(kind) {
  return (CREDIT_PURPOSES.find(p => p.value === kind) || {}).label || null;
}

/** A fresh id for one submission (one click of Allocate) — the server's Idempotency-Key nonce. */
export function newRequestId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) { /* older browsers */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// ── A credit raised against a bill (Jade, 2026-10-13) ───────────────────────

const money = (n) => formatCurrency(round2(n));

/** The bill a credit is raised against: the one bill it relates to, else null. */
export function againstBillOf(billRefs) {
  return Array.isArray(billRefs) && billRefs.length === 1 && billRefs[0]?.id ? billRefs[0] : null;
}

/**
 * Why Search POs' "Add supplier credit" is greyed on a PO row, or null. A
 * billed PO is fine (that is when credits come); a PO-H too (the haulier's
 * credit). Only an archived / voided PO, one not saved here, or one with no
 * supplier is refused.
 */
export function addCreditPoRowBlock(po) {
  if (!po) return 'No PO';
  if (!po.dbId) return 'Not saved here yet — sync POs first';
  const st = up(po.status);
  if (st === 'DELETED') return 'This PO is archived';
  if (st === 'VOIDED') return 'This PO is voided';
  if (!po.contact?.accountId && !po.contact?.contactID) return 'This PO has no supplier contact to credit from';
  return null;
}

/**
 * Add Supplier Credit's prefill from a PO row: its supplier, the PO under
 * Relates to, and the bills that bill its goods (`goodsBills` on the search
 * row) — one of them is the bill the credit is raised against.
 */
export function creditPrefillFromPo(po) {
  const bills = (Array.isArray(po?.goodsBills) ? po.goodsBills : [])
    .filter(b => b && b.id)
    .map(b => ({ id: Number(b.id), supplier_reference: b.supplier_reference || b.bill_number || null }));
  return {
    account_id: po?.contact?.accountId || null,
    xero_contact_id: po?.contact?.contactID || null,
    supplier_name: po?.contact?.name || '',
    pos: po?.dbId ? [{ id: Number(po.dbId), purchase_order_number: po.purchaseOrderNumber || null, xero_purchase_order_id: po.purchaseOrderID || null }] : [],
    bills
  };
}

/**
 * What the credit window says about the bill the credit is raised against,
 * from GET /api/bills/:id/credit-against (`preview`) and the user's two
 * answers. The server works the same out again when the credit is saved.
 *
 * @returns {{ tone: 'info'|'warn', allocates: boolean, amount: number|null, text: string }}
 */
export function againstBillSummary({ preview, creditTotal = 0, clearQuery = true, allocate = true } = {}) {
  const plan = preview?.plan;
  const bill = preview?.bill || {};
  if (!plan) return { tone: 'info', allocates: false, amount: null, text: '' };
  const label = plan.label || bill.label || 'the bill';
  if (!allocate) {
    return { tone: 'info', allocates: false, amount: null,
      text: `Not allocated automatically — allocate it with "Allocate to bills…" once the credit is in Xero.` };
  }
  if (plan.queried && (!clearQuery || plan.clear_query?.block)) {
    return { tone: 'warn', allocates: false, amount: null,
      text: plan.clear_query?.block
        ? `${label} is queried and its query cannot be cleared from here (${plan.clear_query.block}), so nothing will be allocated to it.`
        : `${label} is queried — a queried bill is a draft, and only an approved bill takes a credit. Clear its query to allocate this credit to it.` };
  }
  if (!plan.allocate) return { tone: 'warn', allocates: false, amount: null, text: `Nothing will be allocated: ${plan.reason}` };
  const waitsFor = plan.waits_for || [];
  const whenText = waitsFor.includes('bill') ? `once the credit and ${label} are both in Xero` : 'once the credit is in Xero';
  // Without costs.view the amounts are not sent (purchase costs): say it without them.
  if (plan.amount_due == null && bill.amount_due == null) {
    return { tone: 'info', allocates: true, amount: null,
      text: `The credit will be allocated to ${label} ${whenText}, up to what ${label} still owes.` };
  }
  const due = round2(plan.amount_due ?? bill.amount_due ?? 0);
  const total = round2(Math.abs(Number(creditTotal) || 0));
  const amount = total > 0 ? Math.min(total, due) : due;
  const billAfter = amount >= due - 0.005 ? 'paid' : 'part paid';
  const creditAfter = total > amount + 0.005 ? `, and ${money(total - amount)} of the credit is left to allocate` : '';
  const when = whenText;
  const off = preview.push_enabled === false ? ' Sending to Xero is switched off just now, so it waits until it is on.' : '';
  return {
    tone: 'info', allocates: true, amount,
    text: `${total > 0 ? money(amount) : `Up to ${money(due)}`} will be allocated to ${label} ${when} — ${label} will then be ${billAfter}${creditAfter}.${off}`
  };
}

/** After the save: what happened to the bill the credit was raised against (the answer's `against_bill`). */
export function againstBillResultMessage(ab) {
  if (!ab || ab.error) return ab?.error ? `The credit is saved, but what it does to its bill could not be worked out (${ab.error}).` : null;
  const label = ab.label || 'the bill';
  const out = [];
  if (ab.query_cleared?.ok) {
    out.push(`${label}'s query was cleared${ab.query_cleared.in_xero ? ' (approved in Xero too)' : ''}.`);
  } else if (ab.query_cleared && !ab.query_cleared.ok) {
    out.push(`${label} is still queried — its query could not be cleared: ${ab.query_cleared.error}`);
  }
  if (ab.allocate) {
    const when = (ab.waits_for || []).includes('bill') ? `once the credit and ${label} are both in Xero` : 'once the credit is in Xero';
    out.push(`${ab.amount != null ? money(ab.amount) : 'The credit'} will be allocated to ${label} ${when}.${ab.push_enabled === false ? ' Sending to Xero is switched off just now, so it waits until it is on.' : ''}`);
  } else if (ab.code && ab.code !== 'NOT_CHOSEN') {
    out.push(`Nothing was allocated to ${label}: ${ab.reason}`);
  }
  return out.length ? out.join(' ') : null;
}

/** The bill window's line for a credit's automatic allocation (xero_push_info.auto_allocation), or null. */
export function autoAllocationText(intent) {
  if (!intent || typeof intent !== 'object') return null;
  const label = intent.bill_label || `bill #${intent.bill_id}`;
  switch (intent.status) {
    case 'pending': return `To be allocated to ${label} automatically once the credit and the bill are both in Xero.`;
    case 'done': return `Allocated ${intent.amount != null ? money(intent.amount) : ''} to ${label} automatically.`.replace('  ', ' ');
    case 'failed': return `Not allocated to ${label} automatically: ${intent.error || 'it could not be made'} Use "Allocate to bills…" once that is put right.`;
    case 'cancelled': return `Not allocated to ${label} automatically: ${intent.reason || 'cancelled'}`;
    default: return null;
  }
}

/** The request that replaces a credit's references: BOTH lists, whole (never a partial list). */
export function creditLinksPayload(pos, bills) {
  return {
    purchase_order_ids: (pos || []).map(p => Number(p.id)).filter(n => Number.isInteger(n) && n > 0),
    credit_bill_ids: (bills || []).map(b => Number(b.id)).filter(n => Number.isInteger(n) && n > 0)
  };
}

/** What a search over a supplier's bills (or credits) looks in — SearchHelp's list. */
export const BILL_SEARCH_FIELDS = Object.freeze([
  'supplier invoice no.', 'bill no.', 'reference', 'PO no. (quoted or linked)', 'supplier', 'date (dd/mm/yyyy)'
]);

/**
 * One bill as a picker lists it: date · supplier invoice no. · total · what
 * it still owes · status — and the supplier when it is another member of the
 * supplier group than `supplierId`.
 */
export function billOptionLabel(b, { supplierId = null } = {}) {
  if (!b) return '';
  const ref = b.supplier_reference || b.bill_number || `Bill #${b.id}`;
  const due = num(b.amount_due ?? ((num(b.total) || 0) - (num(b.amount_paid) || 0)));
  const owes = due != null && due > 0.005 ? `owes ${formatCurrency(due)}` : 'nothing owed';
  const status = `${billStatusLabel(b.status) || '—'}${b.is_queried ? ' (queried)' : ''}`;
  const other = supplierId && b.account_id && Number(b.account_id) !== Number(supplierId) && b.supplier_name ? ` · ${b.supplier_name}` : '';
  return `${b.bill_date ? formatDate(b.bill_date) : '—'} · ${ref} · ${formatCurrency(Math.abs(num(b.total) || 0))} · ${owes} · ${status}${other}`;
}

/** The line under a searched list when there is more than it shows, or null. */
export function moreToNarrowText(matched, shown, { what = 'bills' } = {}) {
  const m = Number(matched) || 0;
  const n = Number(shown) || 0;
  if (m <= n) return null;
  return `Showing the newest ${n.toLocaleString('en-GB')} of ${m.toLocaleString('en-GB')} ${what} — type to narrow (number, amount, date, PO…)`;
}
